import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { ICreateManualQuotation, ILogisticServiceWithProductPrices } from 'types/services';
import { CURRENCIES } from 'types/bussines';
import { NudosButton, NudosSelectButton } from 'components/NudosComponents';
import BordFloatIsland from 'components/BordFloatIsland/BordFloatIsland';
import { createManualQuotation } from 'services/priceEdition/logisticServicesPriceEdition.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import {
  buildPriceEditionPayload,
  EDITION_FORM_DEFAULT_VALUES,
  initializePriceEditionForm,
  updateFormPrices,
  createProductIndexMap
} from 'utils/logisticServiceEdition';
import { FREE_ZONE_SPECIAL_BUSINESS_NAME_TYPES } from 'utils/productDefinitions';
import CommentsModal from './components/CommentsModal/CommentsModal';
import BulkPriceEditionModal from './components/BulkPriceEditionModal/BulkPriceEditionModal';
import LSEditionHeader from './components/LSEditionHeader/LSEditionHeader';
import LSEditionTotals from './components/LSEditionTotals/LSEditionTotals';
import ToolEditionCard from './components/ToolEditionCard/ToolEditionCard';
import { ILogisticServiceEditionForm } from './LogisticServiceEdition.types';
import './LogisticServiceEditionModule.scss';

interface ILogisticServiceEditionModuleProps {
  LSData?: ILogisticServiceWithProductPrices;
  onDataReload?: () => Promise<void>;
}

const LogisticServiceEditionModule = ({ LSData, onDataReload }: ILogisticServiceEditionModuleProps) => {
  const methods = useForm<ILogisticServiceEditionForm>({
    defaultValues: EDITION_FORM_DEFAULT_VALUES
  });
  const { control, handleSubmit, reset, setValue, getValues } = methods;
  const { fields: products } = useFieldArray({
    control,
    name: 'products'
  });
  const history = useHistory();

  const [hasTaxes, setHasTaxes] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  const [showBulkPriceEditionModal, setShowBulkPriceEditionModal] = useState(false);
  const exchangeRate = useWatch({ control, name: 'exchangeRate' });

  const groupedProducts = useMemo(() => {
    const initialGroups: Record<string, Array<{ product: typeof products[0]; index: number }>> = {};
    return products.reduce((groups, product, index) => {
      const productSku = product?.product?.sku || 'Sin sku';
      if (!groups[productSku]) {
        groups[productSku] = [];
      }
      groups[productSku].push({ product, index });
      return groups;
    }, initialGroups);
  }, [products]);

  const getProductIndexMap = useMemo(() => createProductIndexMap(products, selectedItems), [products, selectedItems]);

  const onSubmit = async (data: ILogisticServiceEditionForm) => {
    setIsSaving(true);
    const dataCopy: ILogisticServiceEditionForm = JSON.parse(JSON.stringify(data)); // copia profunda del objeto data para evitar mutaciones y no perder datos
    dataCopy.products = data.products?.map(product => {
      return {
        ...product,
        logisticServiceProductPrices:
          product.logisticServiceProductPrices?.filter(
            service => service.logisticServiceTypeId || service.configClassId
          ) || []
      };
    });
    reset(dataCopy);
    const payload: ICreateManualQuotation = buildPriceEditionPayload(dataCopy, LSData);
    try {
      await createManualQuotation(payload);
      history.push(`/logistic-service/details/${LSData?.id}`, { initialTab: 3 });
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: (
          <>No se pudieron guardar los cambios, intenta de nuevo o comunícate con soporte para obtener ayuda.</>
        )
      });
    } finally {
      setShowCommentsModal(false);
      setIsSaving(false);
    }
  };

  const initializeFormValues = () => {
    reset(initializePriceEditionForm(LSData));
    // no puede existir el specialBusinessNameTypeId 0
    const isFreeZone =
      FREE_ZONE_SPECIAL_BUSINESS_NAME_TYPES.includes(LSData?.billingData?.specialBusinessNameTypeId || 0) &&
      LSData?.destinationProductLocation?.country === LSData?.billingData?.countryCode;
    setHasTaxes(isFreeZone);
  };

  const areAllSelected = (): boolean => {
    if (!products?.length) return false;

    const allSerialsSelected = products?.every((_, index) => selectedItems[`serial-${index}`] === true);
    const allSkusSelected = Object.keys(groupedProducts).every(sku => areAllSerialsSelectedForSku(sku));

    return allSerialsSelected && allSkusSelected;
  };

  const handleSelectAll = () => {
    const shouldSelectAll = !areAllSelected();
    const newSelectedItems: Record<string, boolean> = {};

    products?.forEach((_, index) => {
      newSelectedItems[`serial-${index}`] = shouldSelectAll;
    });

    setSelectedItems(newSelectedItems);
  };

  const toggleSelection = (key: string) => {
    setSelectedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const areAllSerialsSelectedForSku = (productSku: string): boolean => {
    const skuProducts = groupedProducts[productSku] || [];
    if (!skuProducts?.length) return false;
    return skuProducts.every(({ index }) => selectedItems[`serial-${index}`] === true);
  };

  const handleToggleSkuSelection = (productSku: string) => {
    const skuProducts = groupedProducts[productSku] || [];
    const shouldSelectAll = !areAllSerialsSelectedForSku(productSku);

    setSelectedItems(prev => {
      const newSelectedItems = { ...prev };
      skuProducts.forEach(({ index }) => {
        newSelectedItems[`serial-${index}`] = shouldSelectAll;
      });
      return newSelectedItems;
    });
  };

  const getSelectedSerialsCount = (): number => {
    return products?.filter((_, index) => selectedItems[`serial-${index}`] === true)?.length || 0;
  };

  const getSelectedProducts = (): typeof products => {
    return products?.filter((_, index) => selectedItems[`serial-${index}`] === true) || [];
  };

  const handleUpdateFormPrices = async (
    updatedPrices: Array<{
      productIndex: number;
      priceIndex: number;
      priceUsd: number;
      totalValueLocal: number;
      exchangeRate: number;
    }>
  ) => {
    updateFormPrices(updatedPrices, getValues, setValue);
    setShowBulkPriceEditionModal(false);
    setSelectedItems({});
  };

  const selectedProductsCount = getSelectedSerialsCount();
  const bulkPriceEditionModal = (
    <BulkPriceEditionModal
      currency={LSData?.logisticServicePrice?.currency || CURRENCIES?.USD}
      exchangeRate={exchangeRate}
      selectedProducts={getSelectedProducts()}
      productIndexMap={getProductIndexMap}
      onUpdateFormPrices={handleUpdateFormPrices}
      onClose={() => setShowBulkPriceEditionModal(false)}
    />
  );

  useEffect(() => {
    initializeFormValues();
  }, [LSData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormProvider {...methods}>
      <form className="editService" onSubmit={handleSubmit(onSubmit)}>
        {/* Nota: Cuando el modal está abierto, el botón de envío está en CommentsModal. Cuando el modal está cerrado, el botón de envío está en el formulario. */}
        {showCommentsModal && (
          <CommentsModal handleCloseModal={() => setShowCommentsModal(false)} isSaving={isSaving} />
        )}
        <main className="editionContent">
          <LSEditionHeader
            companyName={LSData?.billingData?.name || undefined}
            logisticServiceId={LSData?.id || undefined}
            exchangeRate={Number(LSData?.logisticServicePrice?.exchangeRate) || undefined}
            currency={LSData?.logisticServicePrice?.currency || undefined}
          />
          <div className="headerContainer">
            <h2 className="editLSTitle">Resumen del servicio</h2>
            <div className="selectAllToolsContainer">
              <NudosSelectButton
                iconName="checkmark"
                handleClickButton={handleSelectAll}
                isButtonActive={areAllSelected()}
                componentSize="xs w-8 h-8"
              />
              <span>Seleccionar todas las herramientas</span>
            </div>
          </div>
          <section className="cardsContainer">
            {Object.entries(groupedProducts)?.map(([productSku, products]) => (
              <div className="toolCardContainer" key={productSku}>
                {' '}
                <ToolEditionCard
                  productSku={productSku}
                  products={products}
                  isProductSelected={areAllSerialsSelectedForSku(productSku)}
                  onToggleProductSelection={() => handleToggleSkuSelection(productSku)}
                  selectedItems={selectedItems}
                  onToggleSerialSelection={(index: number) => toggleSelection(`serial-${index}`)}
                />
              </div>
            ))}
          </section>
          <hr className="divider" />
          <LSEditionTotals
            logisticServiceData={LSData}
            currency={LSData?.logisticServicePrice?.currency || CURRENCIES.USD}
            hasTaxes={hasTaxes}
            hasCommission={LSData?.paymentInfo?.paymentTypeInfo?.name?.toLowerCase().includes('tarjeta') || false}
          />
        </main>
        <footer className="formFooter">
          <NudosButton
            buttonText="Cancelar y volver"
            colorPalette="secondary"
            handleClick={() => (LSData?.id ? history.push(`/logistic-service/details/${LSData.id}`) : history.goBack())}
          />
          <NudosButton
            buttonText="Guardar cambios"
            colorPalette="primary"
            type={!LSData?.quoteDocumentUrl ? 'submit' : 'button'}
            handleClick={LSData?.quoteDocumentUrl ? () => setShowCommentsModal(true) : undefined}
            isButtonLoading={isSaving}
          />
        </footer>
      </form>
      {!!selectedProductsCount && (
        <BordFloatIsland
          leftContentCount={selectedProductsCount}
          leftContentText="Productos seleccionados"
          buttonText="Editar precios"
          buttonIcon=""
          isButtonDisabled={selectedProductsCount === 1}
          onClose={() => setSelectedItems({})}
          onButtonClick={() => setShowBulkPriceEditionModal(true)}
          customClassName="editPricesButtonContainer"
          buttonProps={{
            customClassName: 'editPricesButton'
          }}
          modal={bulkPriceEditionModal}
          isModalOpen={showBulkPriceEditionModal}
        />
      )}
    </FormProvider>
  );
};

export default LogisticServiceEditionModule;
